<script setup lang="ts">
import { storeToRefs } from 'pinia';

import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { NutsProductVariant, PurchaseOptions } from '@/utils/product';

defineProps<{ variant: NutsProductVariant }>();

const autoDelivery = defineModel<PurchaseOptions['autoDelivery']>({ required: true });

defineEmits<{ 'auto-delivery-learn-more': [] }>();

const { autoDeliveryChannel } = storeToRefs(useAutoDelivery());
</script>

<template>
  <div>
    <SmallBodyText
      class="flex items-center font-semibold text-black"
      id="delivery-options-legend"
      data-test="delivery-options-label"
    >
      Delivery Options
      <UnstyledButton
        class="ml-1"
        data-promo="1"
        data-promo-creative="Delivery Options"
        data-promo-name="Infographic"
        @click="$emit('auto-delivery-learn-more')"
      >
        <img
          alt="learn more"
          class="object-contain w-4 h-4"
          src="@/assets/pdp/info-icon-gray.svg"
        />
      </UnstyledButton>
    </SmallBodyText>
    <div
      class="flex flex-row w-full mt-2 md:gap-2"
      role="radiogroup"
      aria-labelledby="delivery-options-legend"
      data-test="delivery-options"
    >
      <span
        class="relative inline-flex px-3 py-1.5 border rounded-l-md md:rounded-md h-14 w-full"
        :class="[
          autoDelivery === undefined
            ? 'bg-neutral-100 border-black'
            : 'bg-white border-nuts-neutral-200 hover:bg-zinc-50',
          variant.backordered ? 'border-dashed' : 'border-solid',
        ]"
      >
        <label
          class="flex items-center justify-center w-full m-0 cursor-pointer"
          data-promo="1"
          data-promo-creative="Option: One-time purchase"
          data-test="one-time-delivery-options"
          data-promo-name="Delivery Options"
        >
          <input
            v-model="autoDelivery"
            class="absolute top-0 bottom-0 left-0 right-0 w-full h-full p-0 m-0 rounded-md appearance-none cursor-pointer"
            id="one-time-option"
            name="delivery-options"
            type="radio"
            :value="undefined"
          />
          <SmallBodyText>One-time purchase</SmallBodyText>
        </label>
      </span>
      <span
        class="relative inline-flex px-3 py-1.5 border rounded-r-md md:rounded-md h-14 w-full"
        :class="[
          autoDelivery !== undefined
            ? 'bg-neutral-100 border-black'
            : 'bg-white border-nuts-neutral-200 hover:bg-zinc-50',
          variant.backordered ? 'border-dashed' : 'border-solid',
        ]"
      >
        <label
          class="flex items-center justify-center w-full m-0 cursor-pointer"
          data-test="auto-delivery-delivery-options"
          data-promo="1"
          data-promo-creative="Option: Auto-Delivery"
          data-promo-name="Delivery Options"
        >
          <input
            v-model="autoDelivery"
            class="absolute top-0 bottom-0 left-0 right-0 w-full h-full p-0 m-0 rounded-md appearance-none cursor-pointer"
            id="auto-delivery-option"
            name="delivery-options"
            type="radio"
            :value="autoDeliveryChannel"
          />
          <SmallBodyText
            class="flex flex-col items-center justify-center text-sm font-normal sm:flex-row"
          >
            <span class="inline-flex items-center mr-1">
              <img alt="auto delivery" class="h-3.5" src="@/assets/auto-delivery/logo-gray.svg" />
              <span v-if="!variant.wholesale">:</span>
            </span>
            <span class="inline-flex" v-if="!variant.wholesale">
              Save
              <strong class="ml-1">{{ autoDeliveryChannel.discount }}</strong>
              %
            </span>
          </SmallBodyText>
        </label>
      </span>
    </div>
  </div>
</template>

<style scoped>
p {
  @apply m-0;
}
</style>
